import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';


// Import the comprehensive form modals from main modules
import SalesFormModal from '../../sales-hub/components/SalesFormModal';
import PurchaseFormModal from '../../purchase-center/components/PurchaseFormModal';

const QuickActions = () => {
  const navigate = useNavigate();
  const { userProfile, isAuthenticated } = useAuth();
  const [showTimeEntryModal, setShowTimeEntryModal] = useState(false);
  const [showDownloadOptions, setShowDownloadOptions] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  
  // Replace individual modals with comprehensive form modals
  const [showSalesFormModal, setShowSalesFormModal] = useState(false);
  const [showPurchaseFormModal, setShowPurchaseFormModal] = useState(false);
  const [salesFormType, setSalesFormType] = useState('');
  const [purchaseFormType, setPurchaseFormType] = useState('');
  
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);

  // Live data options from Supabase
  const [customerOptions, setCustomerOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [realMetrics, setRealMetrics] = useState(null);

  const [timeEntry, setTimeEntry] = useState({
    project: '',
    task: '',
    startTime: '',
    endTime: '',
    date: new Date()?.toISOString()?.split('T')?.[0]
  });

  const [customizeSettings, setCustomizeSettings] = useState({
    gridColumns: '3',
    cardSize: 'normal',
    showIcons: true,
    showShortcuts: true,
    confirmActions: true,
    soundEffects: false,
    animatedIcons: true,
    autoSave: true
  });

  // Load real data from Supabase
  useEffect(() => {
    const loadRealOptions = async () => {
      if (!isAuthenticated || !userProfile?.organization_id) return;

      try {
        // Load customers
        const { data: customers, error: customerError } = await supabase
          ?.from('customers')
          ?.select('id, name, customer_number')
          ?.eq('organization_id', userProfile?.organization_id)
          ?.eq('status', 'active')
          ?.limit(20);

        if (!customerError && customers) {
          const customerOpts = customers?.map(customer => ({
            value: customer?.id,
            label: `${customer?.customer_number} - ${customer?.name}`
          }));
          setCustomerOptions(customerOpts);
        }

        // Load vendors
        const { data: vendors, error: vendorError } = await supabase
          ?.from('vendors')
          ?.select('id, name, vendor_number')
          ?.eq('organization_id', userProfile?.organization_id)
          ?.eq('status', 'active')
          ?.limit(20);

        if (!vendorError && vendors) {
          const vendorOpts = vendors?.map(vendor => ({
            value: vendor?.id,
            label: `${vendor?.vendor_number} - ${vendor?.name}`
          }));
          setVendorOptions(vendorOpts);
        }

        // Load financial metrics for downloads
        const { data: invoices } = await supabase
          ?.from('invoices')
          ?.select('total_amount, status')
          ?.eq('organization_id', userProfile?.organization_id);

        const { data: bills } = await supabase
          ?.from('bills')
          ?.select('total_amount, status')
          ?.eq('organization_id', userProfile?.organization_id);

        if (invoices && bills) {
          const revenue = invoices?.reduce((sum, inv) => sum + (inv?.total_amount || 0), 0);
          const expenses = bills?.reduce((sum, bill) => sum + (bill?.total_amount || 0), 0);
          
          setRealMetrics({
            revenue,
            expenses,
            netProfit: revenue - expenses,
            cashPosition: revenue - expenses
          });
        }

      } catch (error) {
        console.error('Error loading real options:', error);
      }
    };

    loadRealOptions();
  }, [isAuthenticated, userProfile?.organization_id]);

  const quickActions = [
    {
      id: 1,
      title: 'Create Invoice',
      description: 'Generate new customer invoice',
      icon: 'FileText',
      color: 'bg-primary',
      action: () => {
        setSalesFormType('invoices');
        setShowSalesFormModal(true);
      },
      shortcut: 'Ctrl+I'
    },
    {
      id: 2,
      title: 'Record Expense',
      description: 'Add business expense',
      icon: 'Receipt',
      color: 'bg-error',
      action: () => {
        setPurchaseFormType('expenses');
        setShowPurchaseFormModal(true);
      },
      shortcut: 'Ctrl+E'
    },
    {
      id: 3,
      title: 'Add Customer',
      description: 'Create new customer profile',
      icon: 'UserPlus',
      color: 'bg-success',
      action: () => {
        setSalesFormType('customers');
        setShowSalesFormModal(true);
      },
      shortcut: 'Ctrl+U'
    },
    {
      id: 4,
      title: 'Download Reports',
      description: 'Export live data as PDF or Excel',
      icon: 'Download',
      color: 'bg-indigo-500',
      action: () => setShowDownloadOptions(true),
      shortcut: 'Ctrl+D'
    },
    {
      id: 5,
      title: 'Generate Report',
      description: 'Create financial report',
      icon: 'BarChart3',
      color: 'bg-accent',
      action: () => navigate('/reports-dashboard'),
      shortcut: 'Ctrl+R'
    },
    {
      id: 6,
      title: 'Time Entry',
      description: 'Log project hours',
      icon: 'Clock',
      color: 'bg-secondary',
      action: () => setShowTimeEntryModal(true),
      shortcut: 'Ctrl+T'
    }
  ];

  // Handle form submissions using the comprehensive modals
  const handleSalesFormSave = async (data) => {
    setCreateLoading(true);
    try {
      // This will be handled by the actual SalesFormModal component
      console.log(`${salesFormType} saved:`, data);
      
      const typeLabel = salesFormType === 'invoices' ? 'Invoice' : 
                       salesFormType === 'customers' ? 'Customer' : 'Item';
      alert(`${typeLabel} created successfully!`);
      
      setShowSalesFormModal(false);
    } catch (error) {
      alert(`Failed to create ${salesFormType}. Please try again.`);
    } finally {
      setCreateLoading(false);
    }
  };

  const handlePurchaseFormSave = async (data) => {
    setCreateLoading(true);
    try {
      // This will be handled by the actual PurchaseFormModal component
      console.log(`${purchaseFormType} saved:`, data);
      
      const typeLabel = purchaseFormType === 'expenses' ? 'Expense' : 
                       purchaseFormType === 'vendors' ? 'Vendor' : 'Item';
      alert(`${typeLabel} created successfully!`);
      
      setShowPurchaseFormModal(false);
    } catch (error) {
      alert(`Failed to create ${purchaseFormType}. Please try again.`);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleActionClick = (action) => {
    action?.action();
  };

  const handleTimeEntrySubmit = () => {
    if (!timeEntry?.project || !timeEntry?.task || !timeEntry?.startTime || !timeEntry?.endTime) {
      alert('Please fill in all required fields');
      return;
    }

    // Calculate duration
    const startDate = new Date(`${timeEntry.date}T${timeEntry.startTime}`);
    const endDate = new Date(`${timeEntry.date}T${timeEntry.endTime}`);
    const duration = (endDate - startDate) / (1000 * 60 * 60); // Convert to hours

    if (duration <= 0) {
      alert('End time must be after start time');
      return;
    }

    // Here you would typically save the time entry to your backend/state
    console.log('Time Entry:', {
      ...timeEntry,
      duration: duration?.toFixed(2)
    });

    // Reset form and close modal
    setTimeEntry({
      project: '',
      task: '',
      startTime: '',
      endTime: '',
      date: new Date()?.toISOString()?.split('T')?.[0]
    });
    setShowTimeEntryModal(false);

    // Show success message
    alert(`Time entry recorded: ${duration?.toFixed(2)} hours`);
  };

  // Download functionality - now uses real data
  const handleQuickDownload = async (format) => {
    setDownloadLoading(true);
    
    try {
      if (format === 'pdf') {
        await generateQuickPDFReport();
      } else if (format === 'xls') {
        await generateQuickXLSReport();
      }
    } catch (error) {
      console.error('Download error:', error);
      alert(`Failed to download ${format?.toUpperCase()} report. Please try again.`);
    } finally {
      setDownloadLoading(false);
      setShowDownloadOptions(false);
    }
  };

  const generateQuickPDFReport = async () => {
    const { jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    
    const doc = new jsPDF();
    
    // Header
    doc?.setFontSize(18);
    doc?.text('Quick Financial Summary', 20, 20);
    
    doc?.setFontSize(10);
    doc?.text(`Generated: ${new Date()?.toLocaleString()}`, 20, 30);
    doc?.text(`Organization: ${userProfile?.organization_name || 'Current Organization'}`, 20, 40);
    
    // Real metrics data
    const quickData = realMetrics ? [
      ['Revenue', `$${realMetrics?.revenue?.toLocaleString()}`],
      ['Expenses', `$${realMetrics?.expenses?.toLocaleString()}`],
      ['Net Profit', `$${realMetrics?.netProfit?.toLocaleString()}`],
      ['Cash Position', `$${realMetrics?.cashPosition?.toLocaleString()}`]
    ] : [
      ['Revenue', '$0'],
      ['Expenses', '$0'],
      ['Net Profit', '$0'],
      ['Cash Position', '$0']
    ];
    
    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: quickData,
      startY: 50,
      headStyles: { fillColor: [99, 102, 241] },
      styles: { fontSize: 9 }
    });
    
    doc?.setFontSize(8);
    doc?.text('Generated from live Supabase data', 20, doc?.lastAutoTable?.finalY + 10);
    
    doc?.save(`Quick_Summary_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`);
  };

  const generateQuickXLSReport = async () => {
    const XLSX = await import('xlsx');
    
    const quickData = [
      ['Quick Financial Summary'],
      [`Generated: ${new Date()?.toLocaleString()}`],
      [`Organization: ${userProfile?.organization_name || 'Current Organization'}`],
      [''],
      ['Metric', 'Value'],
      ...(realMetrics ? [
        ['Revenue', `$${realMetrics?.revenue?.toLocaleString()}`],
        ['Expenses', `$${realMetrics?.expenses?.toLocaleString()}`], 
        ['Net Profit', `$${realMetrics?.netProfit?.toLocaleString()}`],
        ['Cash Position', `$${realMetrics?.cashPosition?.toLocaleString()}`]
      ] : [
        ['Revenue', '$0'],
        ['Expenses', '$0'],
        ['Net Profit', '$0'],
        ['Cash Position', '$0']
      ]),
      [''],
      ['Data Source: Live Supabase Database'],
      [`Active Customers: ${customerOptions?.length || 0}`],
      [`Active Vendors: ${vendorOptions?.length || 0}`]
    ];
    
    const ws = XLSX?.utils?.aoa_to_sheet(quickData);
    const wb = XLSX?.utils?.book_new();
    XLSX?.utils?.book_append_sheet(wb, ws, 'Summary');
    
    XLSX?.writeFile(wb, `Quick_Summary_${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);
  };

  const handleCustomizeApply = () => {
    // Apply the customization settings
    setShowCustomizeModal(false);
    // You could add a toast notification here for better UX
    console.log('Quick Actions customization applied:', customizeSettings);
  };

  // Enhanced keyboard shortcuts handler
  React.useEffect(() => {
    const handleKeydown = (event) => {
      if (event?.ctrlKey) {
        const actionKey = event?.key?.toUpperCase();
        
        // Quick Actions shortcuts
        const quickAction = quickActions?.find(a => 
          a?.shortcut === `Ctrl+${actionKey}`
        );
        
        if (quickAction) {
          event?.preventDefault();
          quickAction?.action();
          return;
        }

        // Navigation shortcuts
        const navigationMap = {
          '1': '/',
          '2': '/sales-hub',
          '3': '/purchase-center',
          '4': '/banking',
          '5': '/reports-dashboard',
          '6': '/organization-settings'
        };

        if (navigationMap?.[actionKey]) {
          event?.preventDefault();
          navigate(navigationMap?.[actionKey]);
          return;
        }

        // Other shortcuts
        switch (actionKey) {
          case 'F':
            event?.preventDefault();
            // Focus on search input if available
            const searchInput = document.querySelector('input[placeholder*="search" i], input[placeholder*="filter" i]');
            if (searchInput) {
              searchInput?.focus();
            }
            break;
          case '/':
            event?.preventDefault();
            setShowShortcutsModal(true);
            break;
        }
      } else if (event?.key === 'Escape') {
        // Close any open modals
        setShowShortcutsModal(false);
        setShowTimeEntryModal(false);
        setShowDownloadOptions(false);
        setShowCustomizeModal(false);
        setShowSalesFormModal(false);
        setShowPurchaseFormModal(false);
      }
    };

    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
  }, [navigate]);

  // All available shortcuts data
  const allShortcuts = [
    {
      category: 'Quick Actions',
      shortcuts: [
        { key: 'Ctrl+I', action: 'Create Invoice', description: 'Opens invoice creation form' },
        { key: 'Ctrl+E', action: 'Record Expense', description: 'Add new business expense' },
        { key: 'Ctrl+U', action: 'Add Customer', description: 'Create customer profile' },
        { key: 'Ctrl+D', action: 'Download Reports', description: 'Export dashboard data' },
        { key: 'Ctrl+R', action: 'Generate Report', description: 'Open reports dashboard' },
        { key: 'Ctrl+T', action: 'Time Entry', description: 'Log project hours' }
      ]
    },
    {
      category: 'Navigation',
      shortcuts: [
        { key: 'Ctrl+1', action: 'Dashboard', description: 'Go to main dashboard' },
        { key: 'Ctrl+2', action: 'Sales Hub', description: 'Navigate to sales management' },
        { key: 'Ctrl+3', action: 'Purchase Center', description: 'Access purchase management' },
        { key: 'Ctrl+4', action: 'Banking', description: 'Open banking section' },
        { key: 'Ctrl+5', action: 'Reports', description: 'View reports dashboard' },
        { key: 'Ctrl+6', action: 'Settings', description: 'Organization settings' }
      ]
    },
    {
      category: 'General',
      shortcuts: [
        { key: 'Ctrl+S', action: 'Save', description: 'Save current form or changes' },
        { key: 'Ctrl+F', action: 'Search', description: 'Open search functionality' },
        { key: 'Ctrl+N', action: 'New', description: 'Create new item (context-dependent)' },
        { key: 'Ctrl+P', action: 'Print/Preview', description: 'Print or preview document' },
        { key: 'Esc', action: 'Close Modal', description: 'Close any open modal or dialog' },
        { key: 'Ctrl+/', action: 'Show Shortcuts', description: 'Display this shortcuts panel' }
      ]
    },
    {
      category: 'Data Management',
      shortcuts: [
        { key: 'Ctrl+Enter', action: 'Submit Form', description: 'Submit current active form' },
        { key: 'Ctrl+Z', action: 'Undo', description: 'Undo last action (where supported)' },
        { key: 'Ctrl+Y', action: 'Redo', description: 'Redo last undone action' },
        { key: 'Delete', action: 'Delete Item', description: 'Delete selected item (with confirmation)' },
        { key: 'F5', action: 'Refresh', description: 'Refresh current view/data' }
      ]
    }
  ];

  const renderShortcutsModal = () => {
    if (!showShortcutsModal) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-4xl max-h-[80vh] overflow-hidden">
          <div className="p-6 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-foreground flex items-center">
                  <Icon name="Keyboard" size={24} className="mr-3" />
                  All Keyboard Shortcuts
                </h3>
                <p className="text-muted-foreground mt-1">
                  Use these shortcuts to navigate and work more efficiently
                </p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowShortcutsModal(false)}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
          </div>
          
          <div className="p-6 max-h-96 overflow-y-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {allShortcuts?.map((category, index) => (
                <div key={index} className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <h4 className="font-medium text-foreground text-sm uppercase tracking-wide">
                      {category?.category}
                    </h4>
                  </div>
                  
                  <div className="space-y-3">
                    {category?.shortcuts?.map((shortcut, shortcutIndex) => (
                      <div key={shortcutIndex} className="flex items-center justify-between py-2 px-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                        <div className="flex-1">
                          <div className="font-medium text-foreground text-sm">
                            {shortcut?.action}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {shortcut?.description}
                          </div>
                        </div>
                        <kbd className="ml-4 px-3 py-1 bg-background border border-border rounded text-xs font-mono text-foreground shadow-sm">
                          {shortcut?.key}
                        </kbd>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <div className="p-6 border-t border-border bg-muted/20">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <Icon name="Info" size={14} />
                  <span>Press <kbd className="px-2 py-1 bg-background border border-border rounded text-xs">Ctrl+/</kbd> anytime to view shortcuts</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Lightbulb" size={14} />
                  <span>More shortcuts available in each module</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowShortcutsModal(false)}
                >
                  <Icon name="X" size={14} className="mr-1" />
                  Close
                </Button>
                <Button 
                  variant="default" 
                  size="sm"
                  onClick={() => {
                    // Copy shortcuts as text to clipboard
                    const shortcutsText = allShortcuts?.map(category => 
                      `${category?.category}:\n${category?.shortcuts?.map(s => 
                        `${s?.key} - ${s?.action}: ${s?.description}`
                      )?.join('\n')}`
                    )?.join('\n\n');
                    
                    navigator?.clipboard?.writeText(shortcutsText)?.then(() => {
                      // Show success feedback
                      const button = event?.target?.closest('button');
                      const originalText = button?.textContent;
                      if (button) {
                        button.textContent = 'Copied!';
                        setTimeout(() => {
                          button.textContent = originalText;
                        }, 2000);
                      }
                    });
                  }}
                >
                  <Icon name="Copy" size={14} className="mr-1" />
                  Copy List
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderDownloadModal = () => {
    if (!showDownloadOptions) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-surface border border-border rounded-lg shadow-elevated w-full max-w-sm">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="Download" size={18} className="mr-2" />
                Quick Download
              </h3>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowDownloadOptions(false)}
                disabled={downloadLoading}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
          </div>
          
          <div className="p-4 space-y-3">
            <p className="text-sm text-muted-foreground mb-4">
              Export current dashboard summary with live data
            </p>
            
            <Button
              variant="default"
              onClick={() => handleQuickDownload('pdf')}
              disabled={downloadLoading}
              className="w-full"
              iconName="FileText"
              iconPosition="left"
            >
              {downloadLoading ? 'Generating...' : 'PDF Summary'}
            </Button>
            
            <Button
              variant="outline"
              onClick={() => handleQuickDownload('xls')}
              disabled={downloadLoading}
              className="w-full"
              iconName="Sheet"
              iconPosition="left"
            >
              {downloadLoading ? 'Generating...' : 'Excel Summary'}
            </Button>

            <div className="mt-3 p-3 bg-muted/30 rounded-lg">
              <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                <Icon name="Database" size={12} />
                <span>Reports generated from live database</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="bg-card border border-border rounded-lg shadow-professional">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={18} className="text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground">Quick Actions</h2>
                <p className="text-sm text-muted-foreground">
                  {isAuthenticated ? 'Streamline your accounting workflow' : 'Sign in to access all features'}
                </p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowCustomizeModal(true)}
              disabled={!isAuthenticated}
            >
              <Icon name="Settings" size={16} />
              Customize
            </Button>
          </div>
        </div>
        <div className="p-6">
          <div className={`grid gap-4 ${
            customizeSettings?.gridColumns === '2' ? 'grid-cols-1 md:grid-cols-2' :
            customizeSettings?.gridColumns === '3' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' :
            customizeSettings?.gridColumns === '4'? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'
          }`}>
            {quickActions?.map((action) => (
              <button
                key={action?.id}
                onClick={() => action?.action()}
                disabled={!isAuthenticated}
                className={`group ${
                  customizeSettings?.cardSize === 'compact' ? 'p-3' :
                  customizeSettings?.cardSize === 'large' ? 'p-6' : 'p-4'
                } bg-muted/30 hover:bg-muted/50 rounded-lg border border-transparent hover:border-border transition-all duration-200 text-left ${
                  !isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <div className="flex items-start space-x-3">
                  {customizeSettings?.showIcons && (
                    <div className={`${
                      customizeSettings?.cardSize === 'compact' ? 'w-8 h-8' :
                      customizeSettings?.cardSize === 'large' ? 'w-12 h-12' : 'w-10 h-10'
                    } ${action?.color} rounded-lg flex items-center justify-center flex-shrink-0 ${
                      customizeSettings?.animatedIcons ? 'group-hover:scale-105 transition-transform duration-200' : ''
                    }`}>
                      <Icon 
                        name={action?.icon} 
                        size={customizeSettings?.cardSize === 'compact' ? 16 : customizeSettings?.cardSize === 'large' ? 24 : 20} 
                        color="white" 
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-medium text-foreground group-hover:text-primary transition-colors duration-200 ${
                      customizeSettings?.cardSize === 'compact' ? 'text-sm' :
                      customizeSettings?.cardSize === 'large' ? 'text-lg' : 'text-base'
                    }`}>
                      {action?.title}
                    </h3>
                    <p className={`text-muted-foreground mt-1 line-clamp-2 ${
                      customizeSettings?.cardSize === 'compact' ? 'text-xs' :
                      customizeSettings?.cardSize === 'large' ? 'text-sm' : 'text-sm'
                    }`}>
                      {action?.description}
                    </p>
                    {customizeSettings?.showShortcuts && (
                      <div className="flex items-center justify-between mt-2">
                        <kbd className={`px-2 py-1 bg-muted text-muted-foreground rounded ${
                          customizeSettings?.cardSize === 'compact' ? 'text-xs' : 'text-xs'
                        }`}>
                          {action?.shortcut}
                        </kbd>
                        <Icon 
                          name="ArrowRight" 
                          size={14} 
                          className="text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-200" 
                        />
                      </div>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {isAuthenticated ? (
                  <div className="flex items-center space-x-2">
                    <Icon name="Database" size={12} />
                    <span>Connected to live database</span>
                    <span>•</span>
                    <span>{customerOptions?.length || 0} active customers</span>
                    <span>•</span>
                    <span>{vendorOptions?.length || 0} active vendors</span>
                  </div>
                ) : (
                  'Sign in to access keyboard shortcuts and quick actions'
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowShortcutsModal(true)}
                  disabled={!isAuthenticated}
                >
                  <Icon name="Keyboard" size={16} />
                  Shortcuts
                </Button>
                {isAuthenticated && (
                  <span className="text-xs text-green-600 flex items-center">
                    <Icon name="Wifi" size={12} className="mr-1" />
                    Live Data
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Use comprehensive Sales Form Modal for Invoice and Customer creation */}
      <SalesFormModal
        isOpen={showSalesFormModal}
        onClose={() => setShowSalesFormModal(false)}
        type={salesFormType}
        item={null}
        onSave={handleSalesFormSave}
        customerOptions={customerOptions}
      />

      {/* Use comprehensive Purchase Form Modal for Expense recording */}
      <PurchaseFormModal
        isOpen={showPurchaseFormModal}
        onClose={() => setShowPurchaseFormModal(false)}
        type={purchaseFormType}
        item={null}
        onSave={handlePurchaseFormSave}
        vendorOptions={vendorOptions}
      />

      {/* All Shortcuts Modal */}
      {renderShortcutsModal()}

      {/* Download Options Modal */}
      {renderDownloadModal()}

      {/* Quick Actions Customize Modal */}
      {showCustomizeModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-elevated w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-foreground flex items-center">
                    <Icon name="Settings" size={24} className="mr-3" />
                    Customize Quick Actions
                  </h3>
                  <p className="text-muted-foreground mt-1">
                    Personalize your quick actions layout, shortcuts, and preferences
                  </p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowCustomizeModal(false)}
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-6 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Layout Settings */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-foreground flex items-center">
                    <Icon name="Layout" size={18} className="mr-2" />
                    Layout & Display
                  </h4>
                  
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Grid Columns</label>
                    <select 
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      value={customizeSettings?.gridColumns}
                      onChange={(e) => setCustomizeSettings({...customizeSettings, gridColumns: e?.target?.value})}
                    >
                      <option value="2">2 Columns</option>
                      <option value="3">3 Columns (Default)</option>
                      <option value="4">4 Columns</option>
                      <option value="6">6 Columns</option>
                    </select>
                  </div>
                  
                  <div className="space-y-3">
                    <label className="text-sm font-medium text-foreground">Card Size</label>
                    <select 
                      className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      value={customizeSettings?.cardSize}
                      onChange={(e) => setCustomizeSettings({...customizeSettings, cardSize: e?.target?.value})}
                    >
                      <option value="compact">Compact</option>
                      <option value="normal">Normal (Default)</option>
                      <option value="large">Large</option>
                    </select>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="showIcons"
                      checked={customizeSettings?.showIcons}
                      onChange={(e) => setCustomizeSettings({...customizeSettings, showIcons: e?.target?.checked})}
                      className="rounded border border-border"
                    />
                    <label htmlFor="showIcons" className="text-sm font-medium text-foreground">
                      Show Action Icons
                    </label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="showShortcuts"
                      checked={customizeSettings?.showShortcuts}
                      onChange={(e) => setCustomizeSettings({...customizeSettings, showShortcuts: e?.target?.checked})}
                      className="rounded border border-border"
                    />
                    <label htmlFor="showShortcuts" className="text-sm font-medium text-foreground">
                      Show Keyboard Shortcuts
                    </label>
                  </div>
                </div>

                {/* Action Management */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-foreground flex items-center">
                    <Icon name="Zap" size={18} className="mr-2" />
                    Action Management
                  </h4>
                  
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {quickActions?.map((action) => (
                      <div key={action?.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-8 h-8 ${action?.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <Icon name={action?.icon} size={16} color="white" />
                          </div>
                          <div>
                            <p className="font-medium text-foreground text-sm">{action?.title}</p>
                            <p className="text-xs text-muted-foreground">{action?.shortcut}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button variant="ghost" size="xs" title="Move up">
                            <Icon name="ChevronUp" size={14} />
                          </Button>
                          <Button variant="ghost" size="xs" title="Move down">
                            <Icon name="ChevronDown" size={14} />
                          </Button>
                          <input
                            type="checkbox"
                            defaultChecked
                            className="rounded border border-border"
                            title="Enable/Disable action"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <Button variant="outline" size="sm" className="w-full">
                    <Icon name="Plus" size={14} className="mr-2" />
                    Add Custom Action
                  </Button>
                </div>
              </div>

              {/* Additional Preferences */}
              <div className="space-y-4">
                <h4 className="font-semibold text-foreground flex items-center">
                  <Icon name="User" size={18} className="mr-2" />
                  Personal Preferences
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="confirmActions"
                      checked={customizeSettings?.confirmActions}
                      onChange={(e) => setCustomizeSettings({...customizeSettings, confirmActions: e?.target?.checked})}
                      className="rounded border border-border"
                    />
                    <label htmlFor="confirmActions" className="text-sm font-medium text-foreground">
                      Confirm destructive actions
                    </label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="soundEffects"
                      checked={customizeSettings?.soundEffects}
                      onChange={(e) => setCustomizeSettings({...customizeSettings, soundEffects: e?.target?.checked})}
                      className="rounded border border-border"
                    />
                    <label htmlFor="soundEffects" className="text-sm font-medium text-foreground">
                      Enable sound effects
                    </label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="animatedIcons"
                      checked={customizeSettings?.animatedIcons}
                      onChange={(e) => setCustomizeSettings({...customizeSettings, animatedIcons: e?.target?.checked})}
                      className="rounded border border-border"
                    />
                    <label htmlFor="animatedIcons" className="text-sm font-medium text-foreground">
                      Animated hover effects
                    </label>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="autoSave"
                      checked={customizeSettings?.autoSave}
                      onChange={(e) => setCustomizeSettings({...customizeSettings, autoSave: e?.target?.checked})}
                      className="rounded border border-border"
                    />
                    <label htmlFor="autoSave" className="text-sm font-medium text-foreground">
                      Auto-save preferences
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-border bg-muted/20">
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Changes will be saved automatically and applied immediately
                </div>
                <div className="flex items-center space-x-3">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      // Reset to defaults
                      setCustomizeSettings({
                        gridColumns: '3',
                        cardSize: 'normal',
                        showIcons: true,
                        showShortcuts: true,
                        confirmActions: true,
                        soundEffects: false,
                        animatedIcons: true,
                        autoSave: true
                      });
                    }}
                  >
                    Reset to Defaults
                  </Button>
                  <Button 
                    variant="default"
                    onClick={handleCustomizeApply}
                    iconName="Check"
                    iconPosition="left"
                  >
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Time Entry Modal */}
      {showTimeEntryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg shadow-elevated w-full max-w-md mx-4">
            <div className="p-6 border-b border-border">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground flex items-center">
                  <Icon name="Clock" size={20} className="mr-2" />
                  Log Time Entry
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setShowTimeEntryModal(false)}
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <Input
                label="Project Description *"
                type="text"
                placeholder="Enter project name"
                value={timeEntry?.project}
                onChange={(e) => setTimeEntry({...timeEntry, project: e?.target?.value})}
                required
              />
              
              <Input
                label="Task Description *"
                type="text"
                placeholder="What did you work on?"
                value={timeEntry?.task}
                onChange={(e) => setTimeEntry({...timeEntry, task: e?.target?.value})}
                required
              />
              
              <Input
                label="Date"
                type="date"
                value={timeEntry?.date}
                onChange={(e) => setTimeEntry({...timeEntry, date: e?.target?.value})}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Start Time *"
                  type="time"
                  value={timeEntry?.startTime}
                  onChange={(e) => setTimeEntry({...timeEntry, startTime: e?.target?.value})}
                  required
                />
                
                <Input
                  label="End Time *"
                  type="time"
                  value={timeEntry?.endTime}
                  onChange={(e) => setTimeEntry({...timeEntry, endTime: e?.target?.value})}
                  required
                />
              </div>
            </div>
            
            <div className="p-6 border-t border-border flex space-x-3">
              <Button
                variant="default"
                onClick={handleTimeEntrySubmit}
                className="flex-1"
                iconName="Check"
                iconPosition="left"
              >
                Log Time Entry
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowTimeEntryModal(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickActions;